/**
 * Parses the category field from a campaign
 * Handles both single category (string) and multiple categories (JSON string array)
 * @param {string} category - The category field from campaign data
 * @returns {Array<string>} - Array of category strings
 */
export const parseCategories = (category) => {
  if (!category) return [];
  
  // If it's already an array, return it
  if (Array.isArray(category)) {
    return category;
  }
  
  // If it's a string, try to parse it
  if (typeof category === 'string') {
    // Check if it's a JSON string array like "[\"Medical,Elderly,Urgent\"]"
    try {
      const parsed = JSON.parse(category);
      if (Array.isArray(parsed)) {
        // Handle case where array contains comma-separated string like ["Medical,Elderly,Urgent"]
        if (parsed.length === 1 && typeof parsed[0] === 'string' && parsed[0].includes(',')) {
          return parsed[0].split(',').map(cat => cat.trim()).filter(cat => cat);
        }
        // Handle case where array contains individual category strings
        return parsed.map(cat => typeof cat === 'string' ? cat.trim() : String(cat)).filter(cat => cat);
      }
    } catch (e) {
      // If JSON parsing fails, treat it as a single category string
      // Check if it contains commas (comma-separated categories)
      if (category.includes(',')) {
        return category.split(',').map(cat => cat.trim()).filter(cat => cat);
      }
      // Otherwise, it's a single category
      return [category.trim()].filter(cat => cat);
    }
  }
  
  return [];
};

/**
 * Gets the display text for a category
 * Returns "Multiple categories" if there are multiple categories, otherwise returns the single category
 * @param {string} category - The category field from campaign data
 * @returns {string} - Display text for the category
 */
export const getCategoryDisplayText = (category) => {
  const categories = parseCategories(category);
  
  if (categories.length === 0) {
    return '';
  }
  
  if (categories.length === 1) {
    return categories[0];
  }
  
  return 'Multiple categories';
};

/**
 * Checks if a campaign matches a selected category
 * @param {string} campaignCategory - The category field from campaign data
 * @param {string} selectedCategory - The selected category to filter by
 * @returns {boolean} - True if the campaign matches the selected category
 */
export const matchesCategory = (campaignCategory, selectedCategory) => {
  if (!selectedCategory || selectedCategory === 'All') {
    return true;
  }
  
  const categories = parseCategories(campaignCategory);
  return categories.some(cat => 
    cat.toLowerCase() === selectedCategory.toLowerCase()
  );
};

